import { useCallback, useEffect, useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  buildDeviceViewModel,
  buildFleetInventory,
  buildFleetPresentation,
  buildOperationalSummary,
  compareDeviceViewModels,
  connectivityMeta,
  fleetHasContent,
  formatElapsedMs,
  isHistoricalLifecycle,
  lifecycleActionAvailability,
} from "./deviceFleetViewModel";

const API_BASE = "https://api.briolabs.io";
const FLEET_PATH = "/api/v1/provisioning/fleet";
const DEVICE_DETAIL_PATH = "/api/v1/provisioning/fleet/devices";
const REPAIR_PATH = "/api/v1/provisioning/pairing-codes";

async function apiRequest(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(json.message || json.error || json.code || `HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return json;
}

async function fetchFleet(signal) {
  const json = await apiRequest(FLEET_PATH, { method: "GET", signal });
  return (json && json.data !== undefined ? json.data : json) || {};
}

async function fetchDeviceDetail(deviceId, signal) {
  const json = await apiRequest(`${DEVICE_DETAIL_PATH}/${encodeURIComponent(deviceId)}?limit=50`, {
    method: "GET",
    signal,
  });
  return unwrap(json);
}

function actionErrorMessage(error) {
  if (error.status === 401) return "登入狀態已失效，請重新登入。";
  if (error.status === 403 || error.status === 404) return "設備不存在或目前帳號無權操作。";
  if (error.status === 422) return `資料驗證失敗：${error.message}`;
  return `操作失敗：${error.message || "請稍後再試。"}`;
}

function unwrap(json) {
  return (json && json.data !== undefined ? json.data : json) || {};
}

const SENSITIVE_DISPLAY_KEYS = new Set([
  "token",
  "device_token",
  "device_token_hash",
  "authorization",
  "auth",
  "secret",
  "password",
  "fingerprint_hash",
  "ip_address",
  "remote_addr",
  "user_agent",
  "last_ip",
]);

function sanitizeForDisplay(value) {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForDisplay(item));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !SENSITIVE_DISPLAY_KEYS.has(String(key).toLowerCase()))
        .map(([key, item]) => [key, sanitizeForDisplay(item)]),
    );
  }
  return value;
}

const t = {
  bg: "#0F1117", card: "#1A1D27", border: "#2A2E3A",
  accent: "#6C5CE7", accentLight: "#A29BFE", accentBg: "rgba(108,92,231,0.12)",
  success: "#00B894", successBg: "rgba(0,184,148,0.12)",
  warning: "#FDCB6E", warningBg: "rgba(253,203,110,0.15)",
  danger: "#E17055", dangerBg: "rgba(225,112,85,0.12)",
  text: "#E8E8ED", textDim: "#8B8FA3", muted: "#8B8FA3", dim: "#5A5E72",
  mono: "'JetBrains Mono', 'SF Mono', monospace",
};

const lifecycleMeta = {
  ACTIVE: { color: t.success, background: t.successBg },
  RETIRED: { color: t.warning, background: t.warningBg },
  REVOKED: { color: t.danger, background: t.dangerBg },
};

const styles = {
  page: {
    minHeight: "100%",
    padding: "28px",
    background: t.bg,
    color: t.text,
  },
  card: {
    background: t.card,
    border: `1px solid ${t.border}`,
    borderRadius: 14,
  },
  label: {
    color: t.textDim,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  mono: {
    fontFamily: t.mono,
    overflowWrap: "anywhere",
  },
};

const buttonBase = {
  padding: "8px 12px",
  borderRadius: 8,
  border: `1px solid ${t.border}`,
  color: t.text,
  background: "rgba(15,17,23,0.72)",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 800,
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 11px",
  borderRadius: 8,
  border: `1px solid ${t.border}`,
  color: t.text,
  background: t.bg,
  fontSize: 13,
  outline: "none",
};

function valueOrDash(value) {
  return value === null || value === undefined || value === "" ? "—" : String(value);
}

function formatTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("zh-TW", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function normalizeLifecycle(value) {
  return String(value || "UNKNOWN").toUpperCase();
}

function sortDevices(devices, isUnbound) {
  return (Array.isArray(devices) ? devices : [])
    .map((device) => buildDeviceViewModel(device, { isUnbound }))
    .sort(compareDeviceViewModels);
}

function Badge({ children, color = t.textDim, background = `${t.dim}22` }) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 9px",
      borderRadius: 999,
      color,
      background,
      fontSize: 11,
      fontWeight: 800,
      lineHeight: 1.5,
      whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

function LifecycleBadge({ state }) {
  const normalized = normalizeLifecycle(state);
  const meta = lifecycleMeta[normalized] || {
    color: t.textDim,
    background: `${t.dim}22`,
  };
  return <Badge {...meta}>{normalized}</Badge>;
}

function TokenBadge({ status }) {
  const present = status === "present";
  return (
    <Badge
      color={present ? t.success : t.danger}
      background={present ? t.successBg : t.dangerBg}
    >
      {present ? "present" : status === "missing" ? "missing" : "—"}
    </Badge>
  );
}

const connectivityColors = {
  online: { color: t.success, background: t.successBg },
  delayed: { color: t.warning, background: t.warningBg },
  offline: { color: t.danger, background: t.dangerBg },
  never_connected: { color: t.warning, background: t.warningBg },
  unavailable: { color: t.textDim, background: `${t.dim}22` },
};

function ConnectivityBadge({ connectivity }) {
  const state = connectivity?.state || "unavailable";
  const colors = connectivityColors[state] || connectivityColors.unavailable;
  return (
    <Badge {...colors}>
      Player · {connectivity?.label || connectivityMeta(state).label}
    </Badge>
  );
}

function DeviceField({ label, value, mono = false }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ ...styles.label, marginBottom: 5 }}>{label}</div>
      <div
        title={valueOrDash(value)}
        style={{
          ...(mono ? styles.mono : {}),
          color: value ? t.text : t.dim,
          fontSize: 12,
          lineHeight: 1.45,
        }}
      >
        {valueOrDash(value)}
      </div>
    </div>
  );
}

function DeviceRow({
  device,
  venueId,
  venueName,
  zoneId,
  zoneName,
  isUnbound,
  validVenueCount,
  onAction,
}) {
  const alertCount = Array.isArray(device.alerts) ? device.alerts.length : 0;
  const state = normalizeLifecycle(device.lifecycle_state);
  const active = state === "ACTIVE";
  const needsPairing = active && device.token_status === "missing";
  const actionDevice = {
    ...device,
    venue_id: isUnbound ? device.venue_id : (device.venue_id || venueId),
    zone_id: isUnbound ? device.zone_id : (device.zone_id || zoneId || null),
  };
  const actions = lifecycleActionAvailability(actionDevice, { isUnbound, validVenueCount });
  const connectivity = device.connectivity;
  const snapshot = device.latest_reported_snapshot;
  const attentionColor = device.open_incident || connectivity.state === "offline"
    ? t.danger
    : connectivity.state === "delayed" || connectivity.state === "never_connected"
      ? t.warning
      : connectivity.state === "online"
        ? t.success
        : t.dim;
  const venueContext = isUnbound
    ? "未綁定至有效場域"
    : [venueName, venueId].filter(Boolean).join(" · ");
  const zoneContext = isUnbound
    ? (device.zone_id ? `歷史 Zone ID · ${device.zone_id}` : "未指定區域")
    : ([zoneName, device.zone_id || zoneId].filter(Boolean).join(" · ") || "未指定區域");
  const disabledStyle = { opacity: 0.45, cursor: "not-allowed" };
  return (
    <div style={{
      padding: "15px 16px",
      background: active ? "rgba(0,184,148,0.045)" : "rgba(15,17,23,0.38)",
      border: `1px solid ${device.open_incident ? `${t.danger}99` : alertCount > 0 ? `${t.warning}88` : t.border}`,
      borderLeft: `3px solid ${attentionColor}`,
      borderRadius: 10,
    }}>
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 14,
        flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
            {valueOrDash(device.device_name)}
          </div>
          <div style={{ ...styles.mono, color: t.textDim, fontSize: 11 }}>
            {valueOrDash(device.id)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
          {device.open_incident && (
            <Badge color={t.danger} background={t.dangerBg}>
              目前離線事件 · {valueOrDash(device.open_incident.notification_state)}
            </Badge>
          )}
          <Badge
            color={alertCount > 0 ? t.warning : t.textDim}
            background={alertCount > 0 ? t.warningBg : `${t.dim}22`}
          >
            近期警示 {alertCount}
          </Badge>
          <LifecycleBadge state={state} />
          <TokenBadge status={device.token_status} />
          <ConnectivityBadge connectivity={connectivity} />
          {needsPairing && (
            <Badge color={t.warning} background={t.warningBg}>需要配對</Badge>
          )}
        </div>
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))",
        gap: "13px 18px",
      }}>
        <DeviceField label="場域" value={venueContext} />
        <DeviceField label="區域" value={zoneContext} />
        <DeviceField label="Player 最後回報" value={formatTime(connectivity.last_reported_at)} />
        <DeviceField label="距今時間" value={formatElapsedMs(connectivity.elapsed_ms)} />
      </div>
      <div style={{ ...styles.label, margin: "14px 0 9px" }}>
        最近回報快照（非目前 Player 連線判定）
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))",
        gap: "13px 18px",
      }}>
        <DeviceField label="最後同步" value={formatTime(snapshot.last_sync_at)} />
        <DeviceField label="同步狀態" value={snapshot.last_sync_status} />
        <DeviceField label="最後健康回報" value={formatTime(snapshot.last_health_at)} />
        <DeviceField label="App 版本" value={snapshot.app_version} mono />
        <DeviceField label="資料更新時間" value={formatTime(device.updated_at)} />
      </div>
      <div style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        marginTop: 15,
        paddingTop: 13,
        borderTop: `1px solid ${t.border}`,
      }}>
        <button
          type="button"
          onClick={() => onAction("detail", actionDevice, { isUnbound })}
          style={buttonBase}
        >
          詳細
        </button>
        {active && (
          <>
          <button
            type="button"
            onClick={() => onAction("repair", actionDevice, { isUnbound })}
            disabled={!actions.repair}
            title={!actions.repair && isUnbound ? "設備尚未綁定至有效場域，無法重新配對。" : undefined}
            style={{
              ...buttonBase,
              color: needsPairing ? "#fff" : t.accentLight,
              background: needsPairing ? t.accent : t.accentBg,
              borderColor: needsPairing ? t.accent : `${t.accentLight}66`,
              ...(!actions.repair ? disabledStyle : {}),
            }}
          >
            重新配對
          </button>
          <button
            type="button"
            onClick={() => onAction("transfer", actionDevice, { isUnbound })}
            disabled={!actions.transfer}
            title={!actions.transfer ? "目前帳號沒有可用的目標場域，無法轉移設備。" : undefined}
            style={{ ...buttonBase, ...(!actions.transfer ? disabledStyle : {}) }}
          >
            轉移
          </button>
          <button type="button" onClick={() => onAction("replace", actionDevice, { isUnbound })} style={buttonBase}>
            換機
          </button>
          <button
            type="button"
            onClick={() => onAction("retire", actionDevice, { isUnbound })}
            style={{ ...buttonBase, color: t.danger, borderColor: `${t.danger}77` }}
          >
            退租
          </button>
          </>
        )}
      </div>
      {active && isUnbound && (
        <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.55, marginTop: 9 }}>
          尚未綁定至有效場域，重新配對暫不可用。
          {validVenueCount === 0 && " 目前帳號沒有可用的目標場域，轉移暫不可用。"}
        </div>
      )}
    </div>
  );
}

function DeviceCollection({
  devices,
  sectionKey,
  venueId,
  venueName,
  zoneId,
  zoneName,
  isUnbound = false,
  validVenueCount,
  showHistorical,
  onAction,
}) {
  const ordered = useMemo(() => sortDevices(devices, isUnbound), [devices, isUnbound]);
  const visible = ordered.filter((device) => !isHistoricalLifecycle(device.lifecycle_state));
  const history = ordered.filter((device) => isHistoricalLifecycle(device.lifecycle_state));

  if (ordered.length === 0) {
    return <div style={{ color: t.dim, fontSize: 12, padding: "12px 0" }}>此區域沒有設備</div>;
  }

  return (
    <div>
      <div style={{ display: "grid", gap: 9 }}>
        {visible.map((device, index) => (
          <DeviceRow
            key={device.id || `${sectionKey}-visible-${index}`}
            device={device}
            venueId={venueId}
            venueName={venueName}
            zoneId={zoneId}
            zoneName={zoneName}
            isUnbound={isUnbound}
            validVenueCount={validVenueCount}
            onAction={onAction}
          />
        ))}
        {visible.length === 0 && (
          <div style={{ color: t.dim, fontSize: 12, padding: "4px 0" }}>
            此區域沒有啟用中的設備
          </div>
        )}
      </div>
      {history.length > 0 && showHistorical && (
        <div style={{ marginTop: 12 }}>
          <div style={{
            padding: "10px 12px",
            color: t.textDim,
            background: "rgba(15,17,23,0.55)",
            border: `1px solid ${t.border}`,
            borderRadius: 9,
            fontSize: 12,
            fontWeight: 700,
          }}>
            歷史設備（RETIRED / REVOKED） · {history.length}
          </div>
          <div style={{ display: "grid", gap: 9, marginTop: 9 }}>
            {history.map((device, index) => (
              <DeviceRow
                key={device.id || `${sectionKey}-history-${index}`}
                device={device}
                venueId={venueId}
                venueName={venueName}
                zoneId={zoneId}
                zoneName={zoneName}
                isUnbound={isUnbound}
                validVenueCount={validVenueCount}
                onAction={onAction}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ZoneSection({ zone, venueId, venueName, validVenueCount, showHistorical, onAction }) {
  return (
    <section style={{
      padding: 16,
      background: "rgba(15,17,23,0.45)",
      border: `1px solid ${t.border}`,
      borderRadius: 12,
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        marginBottom: 13,
        flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>{valueOrDash(zone.name)}</div>
          <div style={{ ...styles.mono, color: t.textDim, fontSize: 11, marginTop: 4 }}>
            {valueOrDash(zone.id)}
          </div>
        </div>
        <Badge>{valueOrDash(zone.status)}</Badge>
      </div>
      <DeviceCollection
        devices={zone.devices}
        sectionKey={`${venueId}-${zone.id}`}
        venueId={venueId}
        venueName={venueName}
        zoneId={zone.id}
        zoneName={zone.name}
        validVenueCount={validVenueCount}
        showHistorical={showHistorical}
        onAction={onAction}
      />
    </section>
  );
}

function VenueSection({ venue, validVenueCount, showHistorical, onAction }) {
  const zones = Array.isArray(venue.zones) ? venue.zones : [];
  const unzoned = Array.isArray(venue.unzoned_devices) ? venue.unzoned_devices : [];
  return (
    <section style={{ ...styles.card, padding: 20 }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        marginBottom: 16,
        flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{valueOrDash(venue.name)}</div>
          <div style={{ ...styles.mono, color: t.textDim, fontSize: 11, marginTop: 4 }}>
            {valueOrDash(venue.id)}
          </div>
        </div>
        <Badge>{valueOrDash(venue.status)}</Badge>
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {zones.map((zone, index) => (
          <ZoneSection
            key={zone.id || `${venue.id}-zone-${index}`}
            zone={zone}
            venueId={venue.id}
            venueName={venue.name}
            validVenueCount={validVenueCount}
            showHistorical={showHistorical}
            onAction={onAction}
          />
        ))}
        {unzoned.length > 0 && (
          <section style={{
            padding: 16,
            background: t.warningBg,
            border: `1px solid ${t.warning}55`,
            borderRadius: 12,
          }}>
            <div style={{ color: t.warning, fontSize: 14, fontWeight: 800, marginBottom: 13 }}>
              未分區設備 · {unzoned.length}
            </div>
            <DeviceCollection
              devices={unzoned}
              sectionKey={`${venue.id}-unzoned`}
              venueId={venue.id}
              venueName={venue.name}
              zoneId={null}
              zoneName={null}
              validVenueCount={validVenueCount}
              showHistorical={showHistorical}
              onAction={onAction}
            />
          </section>
        )}
        {zones.length === 0 && unzoned.length === 0 && (
          <div style={{ color: t.dim, fontSize: 13, padding: "10px 0" }}>此場域沒有區域或設備</div>
        )}
      </div>
    </section>
  );
}

function UnboundDevicesSection({ devices, validVenueCount, showHistorical, onAction }) {
  if (!Array.isArray(devices) || devices.length === 0) return null;
  const hasVisibleDevice = devices.some(
    (device) => !isHistoricalLifecycle(device?.lifecycle_state),
  );
  if (!hasVisibleDevice && !showHistorical) return null;
  return (
    <section style={{
      ...styles.card,
      padding: 20,
      borderColor: `${t.warning}66`,
      background: `linear-gradient(180deg, ${t.warningBg}, ${t.card})`,
    }}>
      <div style={{ color: t.warning, fontSize: 18, fontWeight: 800 }}>
        未完成場域綁定的設備
      </div>
      <div style={{ color: t.textDim, fontSize: 12, lineHeight: 1.7, margin: "8px 0 16px" }}>
        這些設備屬於目前帳號，但尚未綁定至有效場域，或原場域已刪除／不屬於目前帳號。
      </div>
      <DeviceCollection
        devices={devices}
        sectionKey="unbound-devices"
        venueId={null}
        venueName={null}
        zoneId={null}
        zoneName={null}
        isUnbound
        validVenueCount={validVenueCount}
        showHistorical={showHistorical}
        onAction={onAction}
      />
    </section>
  );
}

function FleetStatusSection({ counts, operationalSummary, showHistorical, onToggleHistorical }) {
  const statusColor = operationalSummary.kind === "healthy"
    ? t.success
    : operationalSummary.kind === "attention"
      ? t.warning
      : t.textDim;
  const inventoryItems = [
    ["啟用中", counts.active, t.success],
    ["歷史設備", counts.historical, t.textDim],
    ["總計", counts.total, t.text],
  ];
  const historicalCount = counts.historical;
  const historicalCopy = historicalCount === 0
    ? "目前顯示全部設備，沒有已停用的歷史設備"
    : showHistorical
      ? `目前顯示全部設備，包含 ${historicalCount} 台已停用設備（RETIRED / REVOKED）`
      : `目前僅顯示啟用中與未分類設備，已隱藏 ${historicalCount} 台歷史設備`;
  return (
    <section style={{ ...styles.card, padding: 20 }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 16,
        flexWrap: "wrap",
      }}>
        <div>
          <div style={{ ...styles.label, marginBottom: 8 }}>機隊狀態</div>
          <div style={{ color: statusColor, fontSize: 16, fontWeight: 800, lineHeight: 1.5 }}>
            {operationalSummary.message}
          </div>
        </div>
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
          {inventoryItems.map(([label, value, color]) => (
            <div key={label}>
              <div style={{ ...styles.label, marginBottom: 6 }}>{label}</div>
              <div style={{ color, fontSize: 22, fontWeight: 800 }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ color: t.textDim, fontSize: 12, lineHeight: 1.7, marginTop: 14 }}>
        {historicalCopy}
      </div>
      {counts.historical > 0 && (
        <label style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          marginTop: 16,
          color: t.textDim,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
        }}>
          <input
            type="checkbox"
            checked={showHistorical}
            onChange={(event) => onToggleHistorical(event.target.checked)}
          />
          顯示已停用設備（{counts.historical}）
        </label>
      )}
    </section>
  );
}

function AlertSummary({ summary, alerts }) {
  const byType = summary?.alerts_by_type && typeof summary.alerts_by_type === "object"
    ? Object.entries(summary.alerts_by_type)
    : [];
  const recent = [...(Array.isArray(alerts) ? alerts : [])].sort((left, right) => {
    const leftTime = new Date(left.created_at || 0).getTime() || 0;
    const rightTime = new Date(right.created_at || 0).getTime() || 0;
    return rightTime - leftTime;
  });
  return (
    <section style={{ ...styles.card, padding: 20 }}>
      <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 14 }}>警示與資料校正</div>
      {byType.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {byType.map(([type, count]) => (
            <Badge key={type} color={t.accentLight} background={t.accentBg}>
              {type} · {Number(count) || 0}
            </Badge>
          ))}
        </div>
      )}
      {recent.length === 0 ? (
        <div style={{ color: t.dim, fontSize: 13 }}>近期沒有警示</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
            <thead>
              <tr>
                {["警示類型", "設備 ID", "時間"].map((label) => (
                  <th key={label} style={{
                    ...styles.label,
                    padding: "0 10px 9px",
                    textAlign: "left",
                    borderBottom: `1px solid ${t.border}`,
                  }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map((alert, index) => (
                <tr key={`${alert.device_id || "alert"}-${alert.created_at || index}`}>
                  <td style={{ padding: "11px 10px", color: t.text, fontSize: 12 }}>
                    {valueOrDash(alert.alert_type)}
                  </td>
                  <td style={{ ...styles.mono, padding: "11px 10px", color: t.textDim, fontSize: 11 }}>
                    {valueOrDash(alert.device_id)}
                  </td>
                  <td style={{ padding: "11px 10px", color: t.textDim, fontSize: 12 }}>
                    {formatTime(alert.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function JsonPreview({ value }) {
  const sanitized = sanitizeForDisplay(value);
  const empty = sanitized === null
    || sanitized === undefined
    || (Array.isArray(sanitized) && sanitized.length === 0)
    || (
      sanitized
      && typeof sanitized === "object"
      && !Array.isArray(sanitized)
      && Object.keys(sanitized).length === 0
    );
  if (empty) return null;
  return (
    <pre style={{
      margin: "10px 0 0",
      padding: 11,
      overflowX: "auto",
      color: t.textDim,
      background: t.bg,
      border: `1px solid ${t.border}`,
      borderRadius: 8,
      fontFamily: t.mono,
      fontSize: 11,
      lineHeight: 1.55,
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
    }}>
      {JSON.stringify(sanitized, null, 2)}
    </pre>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ color: t.text, fontSize: 15, fontWeight: 800 }}>
      {children}
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <section style={{
      padding: 16,
      background: "rgba(15,17,23,0.34)",
      border: `1px solid ${t.border}`,
      borderRadius: 11,
    }}>
      <SectionTitle>{title}</SectionTitle>
      <div style={{ marginTop: 14 }}>{children}</div>
    </section>
  );
}

function DetailKV({ label, value, mono = false, time = false }) {
  const displayed = time ? formatTime(value) : valueOrDash(value);
  const hasValue = value !== null && value !== undefined && value !== "";
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ ...styles.label, marginBottom: 5 }}>{label}</div>
      <div style={{
        ...(mono ? styles.mono : {}),
        color: hasValue ? t.text : t.dim,
        fontSize: 12,
        lineHeight: 1.5,
      }}>
        {displayed}
      </div>
    </div>
  );
}

function DetailKVGrid({ children, minWidth = 150 }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`,
      gap: "13px 18px",
    }}>
      {children}
    </div>
  );
}

function EmptyDetailState({ children }) {
  return <div style={{ color: t.dim, fontSize: 12 }}>{children}</div>;
}

function TimelineList({ items, emptyText, renderItem }) {
  const ordered = [...(Array.isArray(items) ? items : [])].sort((left, right) => {
    const leftTime = new Date(left.created_at || 0).getTime() || 0;
    const rightTime = new Date(right.created_at || 0).getTime() || 0;
    return rightTime - leftTime;
  });
  if (ordered.length === 0) return <EmptyDetailState>{emptyText}</EmptyDetailState>;
  return (
    <div style={{ display: "grid", gap: 9 }}>
      {ordered.map((item, index) => (
        <div
          key={item.id || `${item.created_at || "timeline"}-${index}`}
          style={{
            padding: 12,
            background: t.bg,
            border: `1px solid ${t.border}`,
            borderRadius: 9,
          }}
        >
          {renderItem(item)}
        </div>
      ))}
    </div>
  );
}

function DetailError({ message, onRetry }) {
  return (
    <div style={{
      padding: 14,
      color: t.danger,
      background: t.dangerBg,
      border: `1px solid ${t.danger}55`,
      borderRadius: 9,
      fontSize: 12,
      lineHeight: 1.6,
    }}>
      <div>{message}</div>
      <button
        type="button"
        onClick={onRetry}
        style={{ ...buttonBase, marginTop: 12, color: t.danger, borderColor: `${t.danger}77` }}
      >
        重試
      </button>
    </div>
  );
}

function NotificationEvidenceSection({ title, evidence, emptyText }) {
  return (
    <DetailSection title={title}>
      {!evidence ? (
        <EmptyDetailState>{emptyText}</EmptyDetailState>
      ) : (
        <DetailKVGrid>
          <DetailKV label="incident_id" value={evidence.incident_id} mono />
          <DetailKV label="notification_type" value={evidence.notification_type} />
          <DetailKV label="status" value={evidence.status} />
          <DetailKV label="created_at" value={evidence.created_at} time />
        </DetailKVGrid>
      )}
    </DetailSection>
  );
}

function CanonicalFleetEvidence({ fleetDevice }) {
  const connectivity = fleetDevice.connectivity;
  const incident = fleetDevice.open_incident;
  const notificationEvidence = fleetDevice.notification_evidence;
  const snapshot = fleetDevice.latest_reported_snapshot;
  const observabilityLimits = fleetDevice.observability_limits;
  return (
    <div style={{ display: "grid", gap: 12, marginBottom: 12 }}>
      <DetailSection title="Player 連線狀態">
        <div style={{ display: "grid", gap: 13 }}>
          <div><ConnectivityBadge connectivity={connectivity} /></div>
          <DetailKVGrid>
            <DetailKV label="state" value={connectivity.state} />
            <DetailKV label="最後回報時間" value={connectivity.last_reported_at} time />
            <DetailKV label="距今時間" value={formatElapsedMs(connectivity.elapsed_ms)} />
            <DetailKV label="source" value={connectivity.source} mono />
          </DetailKVGrid>
        </div>
      </DetailSection>

      <DetailSection title="目前離線事件">
        {!incident ? (
          <EmptyDetailState>目前沒有未解決的離線事件</EmptyDetailState>
        ) : (
          <DetailKVGrid>
            <DetailKV label="id" value={incident.id} mono />
            <DetailKV label="opened_at" value={incident.opened_at} time />
            <DetailKV label="notification_state（生命週期狀態）" value={incident.notification_state} />
            <DetailKV label="attempt count" value={incident.offline_attempt_count} />
            <DetailKV label="next attempt" value={incident.offline_next_attempt_at} time />
            <DetailKV label="notified_at" value={incident.offline_notified_at} time />
            <DetailKV label="terminal_at" value={incident.offline_terminal_at} time />
            <DetailKV label="suppressed_at" value={incident.offline_suppressed_at} time />
            <DetailKV label="last_error_code" value={incident.offline_last_error_code} />
          </DetailKVGrid>
        )}
      </DetailSection>

      <NotificationEvidenceSection
        title="目前事件的離線通知"
        evidence={notificationEvidence.offline}
        emptyText="目前事件沒有離線通知投遞證據"
      />

      <NotificationEvidenceSection
        title="最近一次恢復通知"
        evidence={notificationEvidence.recovery}
        emptyText="尚無已解決事件的恢復通知證據"
      />

      <DetailSection title="最近回報快照">
        <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.6, marginBottom: 12 }}>
          歷史同步／健康快照，不代表目前 Player 連線狀態。
        </div>
        <DetailKVGrid>
          <DetailKV label="last_sync_at" value={snapshot.last_sync_at} time />
          <DetailKV label="last_sync_status" value={snapshot.last_sync_status} />
          <DetailKV label="last_health_at" value={snapshot.last_health_at} time />
          <DetailKV label="app_version" value={snapshot.app_version} mono />
        </DetailKVGrid>
      </DetailSection>

      <DetailSection title="可觀測性限制">
        <div style={{ display: "grid", gap: 8, color: t.textDim, fontSize: 12, lineHeight: 1.6 }}>
          <div>
            顯示器電源：{observabilityLimits.display_power === "unknown" ? "系統無法確認" : valueOrDash(observabilityLimits.display_power)}
          </div>
          <div>
            本地快取播放：{observabilityLimits.local_cached_playback === "unknown" ? "系統無法確認" : valueOrDash(observabilityLimits.local_cached_playback)}
          </div>
        </div>
      </DetailSection>
    </div>
  );
}

function DeviceDetailDrawer({ device, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setDetail(null);
    setLoading(true);
    setError("");
    fetchDeviceDetail(device.id, controller.signal)
      .then((data) => setDetail(data))
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        if (requestError.status === 401) {
          setError("登入狀態已失效，請重新登入。");
        } else if (requestError.status === 403 || requestError.status === 404) {
          setError("設備不存在或目前帳號無權檢視。");
        } else {
          setError(`無法載入設備細節：${requestError.message || "請稍後再試。"}`);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [device.id, retryKey]);

  const fleetDevice = device;
  const detailDevice = detail?.device || {};
  const loadedDevice = {
    ...fleetDevice,
    ...detailDevice,
  };
  const venue = detail?.venue;
  const zone = detail?.zone;
  const health = detail?.health_latest;
  const basicFields = [
    ["Device ID", loadedDevice.id, true],
    ["Device name", loadedDevice.device_name],
    ["lifecycle_state", loadedDevice.lifecycle_state],
    ["token_status", loadedDevice.token_status],
    ["detail.connection_state（歷史／非目前連線判定）", loadedDevice.connection_state],
    ["venue_id", loadedDevice.venue_id, true],
    ["zone_id", loadedDevice.zone_id, true],
    ["screen_id", loadedDevice.screen_id, true],
    ["group_id", loadedDevice.group_id, true],
    ["hw_id", loadedDevice.hw_id, true],
    ["device_type", loadedDevice.device_type],
    ["platform", loadedDevice.platform],
    ["app_version", loadedDevice.app_version, true],
    ["os_version", loadedDevice.os_version, true],
    ["pairing_code_id", loadedDevice.pairing_code_id, true],
  ];
  const basicTimes = [
    ["registered_at", loadedDevice.registered_at],
    ["assigned_at", loadedDevice.assigned_at],
    ["retired_at", loadedDevice.retired_at],
    ["revoked_at", loadedDevice.revoked_at],
    ["token_issued_at", loadedDevice.token_issued_at],
    ["token_last_seen_at", loadedDevice.token_last_seen_at],
    ["created_at", loadedDevice.created_at],
    ["updated_at", loadedDevice.updated_at],
  ];
  const healthFields = health ? [
    ["health_status", health.health_status],
    ["online_status", health.online_status],
    ["last_sync_status", health.last_sync_status],
    ["last_sync_at", formatTime(health.last_sync_at)],
    ["last_upload_status", health.last_upload_status],
    ["last_upload_at", formatTime(health.last_upload_at)],
    ["player_version", health.player_version, true],
    ["thermal_status", health.thermal_status],
    ["fallback_mode", health.fallback_mode],
    ["device_pairing_required", health.device_pairing_required],
    ["reported_at", formatTime(health.reported_at)],
    ["updated_at", formatTime(health.updated_at)],
  ] : [];

  return (
    <div
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        justifyContent: "flex-end",
        background: "rgba(0,0,0,0.72)",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="設備詳細資料"
        style={{
          width: "min(560px, 100%)",
          height: "100vh",
          boxSizing: "border-box",
          overflowY: "auto",
          padding: "20px clamp(16px, 4vw, 24px) 32px",
          background: t.card,
          borderLeft: `1px solid ${t.border}`,
          boxShadow: "-24px 0 80px rgba(0,0,0,0.35)",
        }}
      >
        <header style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 14,
          marginBottom: 20,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>
              {valueOrDash(loadedDevice.device_name)}
            </div>
            <div style={{ ...styles.mono, color: t.textDim, fontSize: 11, marginTop: 5 }}>
              {valueOrDash(loadedDevice.id)}
            </div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 10 }}>
              <LifecycleBadge state={loadedDevice.lifecycle_state} />
              <TokenBadge status={loadedDevice.token_status} />
            </div>
            <div style={{ color: t.textDim, fontSize: 12, lineHeight: 1.6, marginTop: 10 }}>
              {fleetDevice.is_unbound ? (
                <>
                  <div>未綁定至有效場域</div>
                  <div style={styles.mono}>歷史 Venue ID · {valueOrDash(fleetDevice.venue_id)}</div>
                </>
              ) : (
                <>
                  <div>{valueOrDash(venue?.name)} · <span style={styles.mono}>{valueOrDash(venue?.id || loadedDevice.venue_id)}</span></div>
                  <div>
                    {zone
                      ? <>{valueOrDash(zone.name)} · <span style={styles.mono}>{valueOrDash(zone.id)}</span></>
                      : "未指定區域"}
                  </div>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉"
            style={{ ...buttonBase, padding: "5px 9px", color: t.textDim, flex: "0 0 auto" }}
          >
            ×
          </button>
        </header>

        <CanonicalFleetEvidence fleetDevice={fleetDevice} />

        {loading && <EmptyDetailState>正在讀取設備細節…</EmptyDetailState>}
        {!loading && error && (
          <DetailError message={error} onRetry={() => setRetryKey((value) => value + 1)} />
        )}
        {!loading && !error && detail && (
          <div style={{ display: "grid", gap: 12 }}>
            <DetailSection title="設備基本資訊">
              <DetailKVGrid>
                {basicFields.map(([label, value, mono]) => (
                  <DetailKV key={label} label={label} value={value} mono={mono} />
                ))}
                <DetailKV label="revoked_reason" value={loadedDevice.revoked_reason} />
                {basicTimes.map(([label, value]) => (
                  <DetailKV key={label} label={label} value={value} time />
                ))}
              </DetailKVGrid>
            </DetailSection>

            <DetailSection title="最近回報的歷史健康資料">
              <div style={{ color: t.textDim, fontSize: 11, lineHeight: 1.6, marginBottom: 12 }}>
                此區為 detail endpoint 的歷史健康資料，不是目前 Player 連線狀態的判定依據。
              </div>
              {!health ? (
                <EmptyDetailState>尚無健康回報</EmptyDetailState>
              ) : (
                <div style={{ display: "grid", gap: 16 }}>
                  <DetailKVGrid>
                    {healthFields.map(([label, value, mono]) => (
                      <DetailKV key={label} label={label} value={value} mono={mono} />
                    ))}
                  </DetailKVGrid>
                  <div>
                    <div style={{ ...styles.label, marginBottom: 9 }}>Cache</div>
                    <DetailKVGrid minWidth={80}>
                      {["ready", "missing", "corrupt", "failed", "total"].map((field) => (
                        <DetailKV key={field} label={field} value={health.cache_summary?.[field]} />
                      ))}
                    </DetailKVGrid>
                  </div>
                  <div>
                    <div style={{ ...styles.label, marginBottom: 9 }}>Proof</div>
                    <DetailKVGrid minWidth={105}>
                      {["pending", "uploading", "uploaded", "dead_letter", "oldest_pending_age_ms"].map((field) => (
                        <DetailKV key={field} label={field} value={health.proof_summary?.[field]} />
                      ))}
                    </DetailKVGrid>
                  </div>
                  <div>
                    <div style={{ ...styles.label, marginBottom: 9 }}>Storage</div>
                    <DetailKVGrid minWidth={110}>
                      <DetailKV label="free_mb" value={health.storage?.free_mb} />
                      <DetailKV label="total_mb" value={health.storage?.total_mb} />
                    </DetailKVGrid>
                  </div>
                  <JsonPreview value={health.metadata} />
                </div>
              )}
            </DetailSection>

            <DetailSection title="生命週期事件">
              <TimelineList
                items={detail.lifecycle_events}
                emptyText="尚無生命週期事件"
                renderItem={(event) => (
                  <>
                    <DetailKVGrid>
                      <DetailKV label="event_type" value={event.event_type} />
                      <DetailKV label="state_before → state_after" value={`${valueOrDash(event.state_before)} → ${valueOrDash(event.state_after)}`} />
                      <DetailKV label="reason" value={event.reason} />
                      <DetailKV label="actor_id" value={event.actor_id} mono />
                      <DetailKV label="created_at" value={event.created_at} time />
                    </DetailKVGrid>
                    <JsonPreview value={event.metadata} />
                  </>
                )}
              />
            </DetailSection>

            <DetailSection title="指派異動">
              <TimelineList
                items={detail.assignment_history}
                emptyText="尚無指派異動"
                renderItem={(entry) => (
                  <DetailKVGrid>
                    <DetailKV label="venue_id_before → venue_id_after" value={`${valueOrDash(entry.venue_id_before)} → ${valueOrDash(entry.venue_id_after)}`} mono />
                    <DetailKV label="zone_id_before → zone_id_after" value={`${valueOrDash(entry.zone_id_before)} → ${valueOrDash(entry.zone_id_after)}`} mono />
                    <DetailKV label="screen_id_before → screen_id_after" value={`${valueOrDash(entry.screen_id_before)} → ${valueOrDash(entry.screen_id_after)}`} mono />
                    <DetailKV label="group_id_before → group_id_after" value={`${valueOrDash(entry.group_id_before)} → ${valueOrDash(entry.group_id_after)}`} mono />
                    <DetailKV label="reason" value={entry.reason} />
                    <DetailKV label="actor_id" value={entry.actor_id} mono />
                    <DetailKV label="created_at" value={entry.created_at} time />
                  </DetailKVGrid>
                )}
              />
            </DetailSection>

            <DetailSection title="同步拒絕紀錄">
              <TimelineList
                items={detail.sync_rejections}
                emptyText="尚無同步拒絕紀錄"
                renderItem={(entry) => (
                  <DetailKVGrid>
                    <DetailKV label="reason" value={entry.reason} />
                    <DetailKV label="method" value={entry.method} mono />
                    <DetailKV label="path" value={entry.path} mono />
                    <DetailKV label="request_id" value={entry.request_id} mono />
                    <DetailKV label="created_at" value={entry.created_at} time />
                  </DetailKVGrid>
                )}
              />
            </DetailSection>

            <DetailSection title="Reconcile 警示">
              <TimelineList
                items={detail.reconciliation_alerts}
                emptyText="尚無 reconcile 警示"
                renderItem={(alert) => (
                  <>
                    <DetailKVGrid>
                      <DetailKV label="alert_type" value={alert.alert_type} />
                      <DetailKV label="severity" value={alert.severity} />
                      <DetailKV label="first_seen_at" value={alert.first_seen_at} time />
                      <DetailKV label="created_at" value={alert.created_at} time />
                    </DetailKVGrid>
                    <JsonPreview value={alert.details} />
                  </>
                )}
              />
            </DetailSection>

            <DetailSection title="配對稽核">
              <TimelineList
                items={detail.pairing_audit}
                emptyText="尚無配對稽核紀錄"
                renderItem={(audit) => (
                  <>
                    <DetailKVGrid>
                      <DetailKV label="action" value={audit.action} />
                      <DetailKV label="status_before → status_after" value={`${valueOrDash(audit.status_before)} → ${valueOrDash(audit.status_after)}`} />
                      <DetailKV label="actor_id" value={audit.actor_id} mono />
                      <DetailKV label="created_at" value={audit.created_at} time />
                    </DetailKVGrid>
                    <JsonPreview value={audit.details} />
                  </>
                )}
              />
            </DetailSection>
          </div>
        )}
      </div>
    </div>
  );
}

function ModalShell({ title, children, onClose }) {
  return (
    <div
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "rgba(0,0,0,0.72)",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          width: "min(560px, 100%)",
          maxHeight: "calc(100vh - 40px)",
          overflowY: "auto",
          padding: 22,
          background: t.card,
          border: `1px solid ${t.border}`,
          borderRadius: 14,
          boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
        }}
      >
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 18,
        }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{title}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉"
            style={{ ...buttonBase, padding: "5px 9px", color: t.textDim }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, required = false, children }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span style={{ ...styles.label, display: "block", marginBottom: 7 }}>
        {label}{required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}

function ModalError({ message }) {
  if (!message) return null;
  return (
    <div style={{
      marginBottom: 13,
      padding: "10px 12px",
      color: t.danger,
      background: t.dangerBg,
      border: `1px solid ${t.danger}55`,
      borderRadius: 8,
      fontSize: 12,
      lineHeight: 1.55,
    }}>
      {message}
    </div>
  );
}

function SuccessResult({ message, refreshWarning, children, onClose }) {
  return (
    <div>
      <div style={{
        padding: 13,
        color: t.success,
        background: t.successBg,
        border: `1px solid ${t.success}55`,
        borderRadius: 9,
        fontSize: 13,
        lineHeight: 1.65,
      }}>
        {message}
      </div>
      {refreshWarning && (
        <div style={{ color: t.warning, fontSize: 12, lineHeight: 1.6, marginTop: 12 }}>
          {refreshWarning}
        </div>
      )}
      {children}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
        <button type="button" onClick={onClose} style={{ ...buttonBase, background: t.accent }}>
          完成
        </button>
      </div>
    </div>
  );
}

function RetireModal({ device, onClose, executeLifecycle }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await executeLifecycle(
        `/api/v1/deviceops/devices/${encodeURIComponent(device.id)}/retire`,
        { reason: "portal_retire" },
      );
      setResult(response);
    } catch (requestError) {
      setError(actionErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="退租 / 停用設備" onClose={onClose}>
      {result ? (
        <SuccessResult
          message="設備已退租，Token 已失效。"
          refreshWarning={result.refreshWarning}
          onClose={onClose}
        />
      ) : (
        <>
          <div style={{ color: t.textDim, fontSize: 13, lineHeight: 1.7, marginBottom: 14 }}>
            設備 <strong style={{ color: t.text }}>{device.device_name || device.id}</strong> 將離開啟用中的計費與機隊，
            現有 Token 將不再可用。此操作需要明確確認。
          </div>
          <ModalError message={error} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 9 }}>
            <button type="button" onClick={onClose} disabled={busy} style={buttonBase}>取消</button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              style={{ ...buttonBase, color: "#fff", background: t.danger, borderColor: t.danger }}
            >
              {busy ? "處理中…" : "確認退租"}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function ReplaceModal({ device, canRepair, onClose, executeLifecycle, onRepair }) {
  const [form, setForm] = useState({
    new_device_id: "",
    new_device_name: "",
    new_hw_id: "",
    reason: "portal_replace",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.new_device_id.trim()) {
      setError("請輸入新設備 ID。");
      return;
    }
    setBusy(true);
    setError("");
    const body = {
      new_device_id: form.new_device_id.trim(),
      reason: form.reason.trim() || "portal_replace",
    };
    if (form.new_device_name.trim()) body.new_device_name = form.new_device_name.trim();
    if (form.new_hw_id.trim()) body.new_hw_id = form.new_hw_id.trim();
    try {
      const response = await executeLifecycle(
        `/api/v1/deviceops/devices/${encodeURIComponent(device.id)}/replace`,
        body,
      );
      setResult(response);
    } catch (requestError) {
      setError(actionErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="換機" onClose={onClose}>
      {result ? (
        <SuccessResult
          message="換機記錄已建立。請使用配對碼完成新設備啟用。"
          refreshWarning={result.refreshWarning}
          onClose={onClose}
        >
          {canRepair ? (
            <button
              type="button"
              onClick={() => onRepair({
                ...device,
                id: form.new_device_id.trim(),
                device_name: form.new_device_name.trim() || form.new_device_id.trim(),
                token_status: "missing",
              })}
              style={{
                ...buttonBase,
                width: "100%",
                marginTop: 14,
                color: "#fff",
                background: t.accent,
                borderColor: t.accent,
              }}
            >
              產生新設備配對碼
            </button>
          ) : (
            <div style={{ color: t.textDim, fontSize: 12, lineHeight: 1.6, marginTop: 12 }}>
              新設備需先綁定至有效場域，才能產生配對碼。
            </div>
          )}
        </SuccessResult>
      ) : (
        <form onSubmit={submit}>
          <div style={{
            color: t.warning,
            background: t.warningBg,
            border: `1px solid ${t.warning}55`,
            borderRadius: 9,
            padding: 12,
            marginBottom: 15,
            fontSize: 12,
            lineHeight: 1.65,
          }}>
            舊設備將被退役。新設備仍需完成配對並取得 Token 後才能同步。
          </div>
          <Field label="新設備 ID" required>
            <input value={form.new_device_id} onChange={update("new_device_id")} style={inputStyle} />
          </Field>
          <Field label="新設備名稱">
            <input value={form.new_device_name} onChange={update("new_device_name")} style={inputStyle} />
          </Field>
          <Field label="新硬體 ID">
            <input value={form.new_hw_id} onChange={update("new_hw_id")} style={inputStyle} />
          </Field>
          <Field label="原因">
            <input value={form.reason} onChange={update("reason")} style={inputStyle} />
          </Field>
          <ModalError message={error} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 9 }}>
            <button type="button" onClick={onClose} disabled={busy} style={buttonBase}>取消</button>
            <button
              type="submit"
              disabled={busy}
              style={{ ...buttonBase, color: "#fff", background: t.accent, borderColor: t.accent }}
            >
              {busy ? "處理中…" : "確認建立換機記錄"}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

function TransferModal({ device, venues, onClose, executeLifecycle }) {
  const currentVenueIsValid = venues.some((venue) => venue.id === device.venue_id);
  const initialVenueId = currentVenueIsValid ? device.venue_id : (venues[0]?.id || "");
  const [venueId, setVenueId] = useState(initialVenueId);
  const [zoneChoice, setZoneChoice] = useState(
    currentVenueIsValid && device.zone_id ? "__KEEP__" : "__CLEAR__",
  );
  const [reason, setReason] = useState("portal_transfer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const selectedVenue = venues.find((venue) => venue.id === venueId);
  const zones = Array.isArray(selectedVenue?.zones) ? selectedVenue.zones : [];
  const canKeepZone = venueId === device.venue_id && Boolean(device.zone_id);

  const changeVenue = (event) => {
    const nextVenueId = event.target.value;
    setVenueId(nextVenueId);
    setZoneChoice(nextVenueId === device.venue_id && device.zone_id ? "__KEEP__" : "__CLEAR__");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!venues.some((venue) => venue.id === venueId)) {
      setError("請選擇目前機隊資料中的場域。");
      return;
    }
    if (
      zoneChoice !== "__KEEP__"
      && zoneChoice !== "__CLEAR__"
      && !zones.some((zone) => zone.id === zoneChoice)
    ) {
      setError("請選擇該場域中的有效區域。");
      return;
    }
    const body = {
      venue_id: venueId,
      reason: reason.trim() || "portal_transfer",
    };
    if (zoneChoice === "__CLEAR__") body.zone_id = null;
    if (zoneChoice !== "__KEEP__" && zoneChoice !== "__CLEAR__") body.zone_id = zoneChoice;
    setBusy(true);
    setError("");
    try {
      const response = await executeLifecycle(
        `/api/v1/deviceops/devices/${encodeURIComponent(device.id)}/transfer`,
        body,
      );
      setResult(response);
    } catch (requestError) {
      setError(actionErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="轉移設備" onClose={onClose}>
      {result ? (
        <SuccessResult
          message="設備轉移已完成。"
          refreshWarning={result.refreshWarning}
          onClose={onClose}
        />
      ) : (
        <form onSubmit={submit}>
          <Field label="目標場域" required>
            <select value={venueId} onChange={changeVenue} style={inputStyle}>
              {venues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name || venue.id} · {venue.id}
                </option>
              ))}
            </select>
          </Field>
          <Field label="目標區域">
            <select value={zoneChoice} onChange={(event) => setZoneChoice(event.target.value)} style={inputStyle}>
              {canKeepZone && <option value="__KEEP__">保留目前區域 · {device.zone_id}</option>}
              <option value="__CLEAR__">不指定區域 · 清除區域</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>{zone.name || zone.id} · {zone.id}</option>
              ))}
            </select>
          </Field>
          <Field label="原因">
            <input value={reason} onChange={(event) => setReason(event.target.value)} style={inputStyle} />
          </Field>
          <ModalError message={error} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 9 }}>
            <button type="button" onClick={onClose} disabled={busy} style={buttonBase}>取消</button>
            <button
              type="submit"
              disabled={busy || !venueId}
              style={{ ...buttonBase, color: "#fff", background: t.accent, borderColor: t.accent }}
            >
              {busy ? "處理中…" : "確認轉移"}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

function RepairModal({ device, venues, onClose }) {
  const venue = venues.find((item) => item.id === device.venue_id);
  const zones = Array.isArray(venue?.zones) ? venue.zones : [];
  const [zoneId, setZoneId] = useState(device.zone_id || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [code, setCode] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { venue_id: device.venue_id, count: 1 };
      if (zoneId) body.zone_id = zoneId;
      const json = await apiRequest(REPAIR_PATH, { method: "POST", body });
      const data = unwrap(json);
      const generated = Array.isArray(data.codes) ? data.codes[0] : null;
      if (!generated?.code) throw new Error("回應缺少配對碼");
      setCode(generated.code);
    } catch (requestError) {
      setError(actionErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="重新配對" onClose={onClose}>
      {code ? (
        <div>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <div style={{
              ...styles.mono,
              padding: "8px 0 14px",
              color: t.text,
              fontSize: 34,
              fontWeight: 800,
              letterSpacing: 4,
              userSelect: "all",
            }}>
              {code}
            </div>
            <div style={{ display: "inline-flex", padding: 12, background: "#fff", borderRadius: 10 }}>
              <QRCodeCanvas value={code} size={180} marginSize={2} level="M" />
            </div>
          </div>
          <div style={{ color: t.textDim, fontSize: 12, lineHeight: 1.65, textAlign: "center" }}>
            在新設備輸入此配對碼或掃描 QR 完成啟用。此操作不會改變原設備生命週期。
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
            <button type="button" onClick={onClose} style={{ ...buttonBase, background: t.accent }}>
              完成
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ color: t.textDim, fontSize: 13, lineHeight: 1.7, marginBottom: 14 }}>
            為 <strong style={{ color: t.text }}>{device.device_name || device.id}</strong> 產生新的配對碼。
          </div>
          <Field label="場域">
            <input
              value={venue ? `${venue.name || venue.id} · ${venue.id}` : device.venue_id || ""}
              readOnly
              style={{ ...inputStyle, color: t.textDim }}
            />
          </Field>
          <Field label="配對區域">
            <select value={zoneId} onChange={(event) => setZoneId(event.target.value)} style={inputStyle}>
              <option value="">整個場域 · venue-wide</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>{zone.name || zone.id} · {zone.id}</option>
              ))}
            </select>
          </Field>
          <ModalError message={error} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 9 }}>
            <button type="button" onClick={onClose} disabled={busy} style={buttonBase}>取消</button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !device.venue_id}
              style={{ ...buttonBase, color: "#fff", background: t.accent, borderColor: t.accent }}
            >
              {busy ? "產生中…" : "產生配對碼"}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function StateCard({ title, message, action }) {
  return (
    <div style={{
      ...styles.card,
      maxWidth: 560,
      margin: "80px auto",
      padding: 28,
      textAlign: "center",
    }}>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>{title}</div>
      <div style={{ color: t.textDim, fontSize: 13, lineHeight: 1.65 }}>{message}</div>
      {action}
    </div>
  );
}

export default function DeviceFleetPage() {
  const [fleet, setFleet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [action, setAction] = useState(null);
  const [showHistorical, setShowHistorical] = useState(false);

  const load = useCallback(() => setReloadKey((value) => value + 1), []);
  const executeLifecycle = useCallback(async (path, body) => {
    const response = await apiRequest(path, { method: "POST", body });
    let refreshWarning = "";
    try {
      const refreshedFleet = await fetchFleet();
      setFleet(refreshedFleet);
    } catch (refreshError) {
      refreshWarning = refreshError.status === 401
        ? "操作已完成，但登入狀態已失效，無法重新載入機隊資料。"
        : "操作已完成，但機隊資料重新載入失敗。請關閉視窗後手動重新整理。";
    }
    return { response, refreshWarning };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    fetchFleet(controller.signal)
      .then((data) => setFleet(data))
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setError(
            requestError.status === 401
              ? "登入狀態已失效，請重新登入。"
              : `無法載入設備資料：${requestError.message}`,
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reloadKey]);

  const presentationFleet = useMemo(() => buildFleetPresentation(fleet), [fleet]);
  const venues = Array.isArray(presentationFleet?.venues) ? presentationFleet.venues : [];
  const unboundDevices = Array.isArray(presentationFleet?.unbound_devices)
    ? presentationFleet.unbound_devices
    : [];
  const summary = fleet?.summary || {};
  const inventory = useMemo(() => buildFleetInventory(presentationFleet), [presentationFleet]);
  const operationalSummary = useMemo(
    () => buildOperationalSummary(inventory.activeDevices),
    [inventory],
  );
  const handleAction = (type, device, context = {}) => {
    if (type === "repair" && context.isUnbound) return;
    if (type === "transfer" && venues.length === 0) return;
    setAction({ type, device, isUnbound: context.isUnbound === true });
  };

  if (loading) {
    return (
      <main style={styles.page}>
        <StateCard title="載入設備資料" message="正在讀取場域、區域與完整設備生命週期…" />
      </main>
    );
  }

  if (error) {
    return (
      <main style={styles.page}>
        <StateCard
          title="設備資料載入失敗"
          message={error}
          action={(
            <button type="button" onClick={load} style={{
              marginTop: 18,
              padding: "9px 16px",
              border: 0,
              borderRadius: 8,
              color: "#fff",
              background: t.accent,
              cursor: "pointer",
              fontWeight: 700,
            }}>
              重新載入
            </button>
          )}
        />
      </main>
    );
  }

  return (
    <main style={styles.page}>
      <div style={{ maxWidth: 1440, margin: "0 auto" }}>
        <header style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 22,
          flexWrap: "wrap",
        }}>
          <div>
            <h1 style={{ fontSize: 24, margin: "0 0 7px", fontWeight: 800 }}>設備管理</h1>
            <div style={{ color: t.textDim, fontSize: 13 }}>
              {valueOrDash(fleet?.account?.name || fleet?.account?.account_name)}
              {fleet?.account?.id || fleet?.account?.account_id
                ? ` · ${fleet.account.id || fleet.account.account_id}`
                : ""}
            </div>
          </div>
          <Badge color={t.accentLight} background={t.accentBg}>生命週期管理</Badge>
        </header>

        <div style={{ marginBottom: 18 }}>
          <FleetStatusSection
            counts={inventory.counts}
            operationalSummary={operationalSummary}
            showHistorical={showHistorical}
            onToggleHistorical={setShowHistorical}
          />
        </div>

        <div style={{ display: "grid", gap: 18 }}>
          <AlertSummary summary={summary} alerts={fleet?.recent_alerts} />
          {!fleetHasContent(presentationFleet) ? (
            <StateCard title="尚無設備資料" message="此帳號目前沒有場域、區域或設備生命週期紀錄。" />
          ) : (
            <>
              {venues.map((venue, index) => (
                <VenueSection
                  key={venue.id || `venue-${index}`}
                  venue={venue}
                  validVenueCount={venues.length}
                  showHistorical={showHistorical}
                  onAction={handleAction}
                />
              ))}
              <UnboundDevicesSection
                devices={unboundDevices}
                validVenueCount={venues.length}
                showHistorical={showHistorical}
                onAction={handleAction}
              />
            </>
          )}
        </div>
      </div>
      {action?.type === "detail" && (
        <DeviceDetailDrawer
          device={action.device}
          onClose={() => setAction(null)}
        />
      )}
      {action?.type === "retire" && (
        <RetireModal
          device={action.device}
          onClose={() => setAction(null)}
          executeLifecycle={executeLifecycle}
        />
      )}
      {action?.type === "replace" && (
        <ReplaceModal
          device={action.device}
          canRepair={!action.isUnbound && venues.some((venue) => venue.id === action.device.venue_id)}
          onClose={() => setAction(null)}
          executeLifecycle={executeLifecycle}
          onRepair={(device) => setAction({ type: "repair", device, isUnbound: false })}
        />
      )}
      {action?.type === "transfer" && venues.length > 0 && (
        <TransferModal
          device={action.device}
          venues={venues}
          onClose={() => setAction(null)}
          executeLifecycle={executeLifecycle}
        />
      )}
      {action?.type === "repair"
        && !action.isUnbound
        && venues.some((venue) => venue.id === action.device.venue_id) && (
        <RepairModal
          device={action.device}
          venues={venues}
          onClose={() => setAction(null)}
        />
      )}
    </main>
  );
}
